"use client";

import { ShoppingCartIcon } from "@/features/cart/ui";
import { SearchBar } from "@/features/search/ui";
import { UserAccountNav } from "@/features/user/ui";
import { Link } from "@/i18n/navigation";
import { Box, cn, Grid, HStack, Separator, VStack } from "@/ui";
import { DesktopNavItems } from "./DesktopNavItems";
import { LanguageSelector } from "./LanguageSelector";
import {
  getDesktopCenterContentClassName,
  getDesktopCompactNavItemsClassName,
  getDesktopSearchBarContainerClassName,
} from "./searchBarLayout";

interface DesktopNavProps {
  isCollapsed?: boolean;
}

// Layout grid configurations
const getGridColumns = (isCollapsed: boolean) =>
  isCollapsed ? "grid-cols-[auto_1fr_auto]" : "grid-cols-[1fr_2fr_1fr]";

// Logo size transitions
const getLogoSize = (isCollapsed: boolean) =>
  isCollapsed ? "text-xl lg:text-2xl" : "text-2xl lg:text-3xl";

// Animation transition base classes
const TRANSITION_BASE = "transition-all duration-300 ease-out";

// Container overflow behavior
const getCenterContainerOverflow = (isCollapsed: boolean) =>
  isCollapsed ? "overflow-visible" : "overflow-hidden";

// Divider visibility
const getDividerVisibility = (isCollapsed: boolean) =>
  isCollapsed ? "opacity-0 h-0" : "opacity-100";

// Bottom nav row visibility
const getBottomNavVisibility = (isCollapsed: boolean) =>
  isCollapsed ? "opacity-0 h-0 overflow-hidden" : "opacity-100 overflow-visible";

/**
 * DesktopNav component - Desktop navigation layout
 * Luxury styling: minimal chrome, refined logo, elegant hairline separator
 * Collapses to single line (hides search bar, shows nav items inline) when scrolling down
 */
export const DesktopNav = ({ isCollapsed = false }: DesktopNavProps) => {
  return (
    <VStack className={cn("hidden w-full md:flex relative", isCollapsed ? "gap-0" : "gap-4")}>
      <Grid className={cn("items-center gap-4", getGridColumns(isCollapsed))}>
        <Link
          href="/"
          className={cn(
            "font-serif font-medium tracking-tight text-foreground w-fit",
            TRANSITION_BASE,
            getLogoSize(isCollapsed),
          )}
        >
          TUTULY
        </Link>

        <CenterContent isCollapsed={isCollapsed} />

        <HStack className="justify-end items-center gap-4">
          <UserAccountNav />
          <ShoppingCartIcon />
          <LanguageSelector />
        </HStack>
      </Grid>

      <Separator
        className={cn("bg-border/30 h-px", TRANSITION_BASE, getDividerVisibility(isCollapsed))}
      />

      <Box className={cn(TRANSITION_BASE, "relative", getBottomNavVisibility(isCollapsed))}>
        <DesktopNavItems />
      </Box>
    </VStack>
  );
};

/**
 * CenterContent - Handles the swap between SearchBar and compact NavItems
 */
function CenterContent({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <Box
      className={cn(
        getDesktopCenterContentClassName(isCollapsed),
        getCenterContainerOverflow(isCollapsed),
      )}
    >
      <Box className={getDesktopSearchBarContainerClassName(isCollapsed)}>
        <SearchBar />
      </Box>

      <Box className={getDesktopCompactNavItemsClassName(isCollapsed)}>
        <DesktopNavItems compact />
      </Box>
    </Box>
  );
}
